import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';

import { AppState } from 'src';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import {
  CILNotificationSelection,
  cilNotificationService,
  CILNotificationViewModel
} from '@services/CILNotificationService';
import { BadRequestError } from '@services/BaseService';

import { CILNotificationComponentPage } from './_components/cil-notification.component';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CILNotificationActionType {
  GetCILNotificationFailure = 'GetCILNotificationFailure',
  GetCILNotificationStart = 'GetCILNotificationStart',
  GetCILNotificationSuccess = 'GetCILNotificationSuccess',
  GetCILNotificationWarn = 'GetCILNotificationWarn',
  GotoPage = 'GotoPage',
  SubmitSelectionFailure = 'SubmitSelectionFailure',
  SubmitSelectionStart = 'SubmitSelectionStart',
  SubmitSelectionSuccess = 'SubmitSelectionSuccess'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface CILNotificationAction extends Action {
  readonly type: CILNotificationActionType;
}

export interface GetCILNotificationStartAction extends CILNotificationAction {
  readonly contractId: number;
  readonly swoTrackingCILOfferId: number;
  readonly token: string;
}

export interface GetCILNotificationSuccessAction extends CILNotificationAction {
  readonly viewModel: CILNotificationViewModel;
}

export interface GetCILNotificationFailureAction extends CILNotificationAction {}

export interface GetCILNotificationWarnAction extends CILNotificationAction {
  readonly message: string;
}

export interface GotoPageAction extends CILNotificationAction {
  readonly page: CILNotificationComponentPage;
}

export interface SubmitSelectionFailureAction extends CILNotificationAction {
  readonly message: string;
}

export interface SubmitSelectionSuccessAction extends CILNotificationAction {
  readonly selection: CILNotificationSelection;
}

export interface SubmitSelectionStartAction extends CILNotificationAction {
  readonly selection: CILNotificationSelection;
  readonly swoTrackingCILOfferId: number;
}

// Actions for each action type declared here.
// They should all return an action object.
export const getCILNotification = (swoTrackingCILOfferId: number, contractId: number, token: string) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getCILNotificationStart(contractId, swoTrackingCILOfferId, token));
    try {
      const cilNotificationViewModel = await cilNotificationService.getCILViewModel(
        swoTrackingCILOfferId,
        contractId,
        token
      );
      dispatch(getCILNotificationSuccess(cilNotificationViewModel));
    } catch (e) {
      if (e instanceof BadRequestError) {
        dispatch(getCILNotificationWarn(e.message));
      } else {
        dispatch(getCILNotificationFailure(e.toString()));
      }
    }
  };
};

export function getCILNotificationFailure(errorMessage: string): GetCILNotificationFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage.toString()} />, ToastOptions.Error);

  return {
    type: CILNotificationActionType.GetCILNotificationFailure
  };
}

export function getCILNotificationStart(
  contractId: number,
  swoTrackingCILOfferId: number,
  token: string
): GetCILNotificationStartAction {
  return {
    type: CILNotificationActionType.GetCILNotificationStart,
    contractId: contractId,
    swoTrackingCILOfferId: swoTrackingCILOfferId,
    token: token
  };
}

export function getCILNotificationSuccess(viewModel: CILNotificationViewModel): GetCILNotificationSuccessAction {
  return {
    type: CILNotificationActionType.GetCILNotificationSuccess,
    viewModel: viewModel
  };
}

export function getCILNotificationWarn(message: string): GetCILNotificationWarnAction {
  return {
    type: CILNotificationActionType.GetCILNotificationWarn,
    message: message
  };
}

export function gotoPage(page: CILNotificationComponentPage): GotoPageAction {
  return {
    type: CILNotificationActionType.GotoPage,
    page: page
  };
}

export const submitSelection = (selection: CILNotificationSelection, swoTrackingCILOfferId: number, token: string) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(submitSelectionStart(selection, swoTrackingCILOfferId));
    try {
      await cilNotificationService.postCILNotificationSelection(swoTrackingCILOfferId, selection, token);
      dispatch(submitSelectionSuccess(selection));
    } catch (e) {
      dispatch(submitSelectionFailure(e.toString()));
    }
  };
};

export function submitSelectionFailure(message: string): SubmitSelectionFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={message} />, ToastOptions.Error);
  return {
    type: CILNotificationActionType.SubmitSelectionFailure,
    message: message
  };
}

export function submitSelectionSuccess(selection: CILNotificationSelection): SubmitSelectionSuccessAction {
  return {
    type: CILNotificationActionType.SubmitSelectionSuccess,
    selection: selection
  };
}

export function submitSelectionStart(
  selection: CILNotificationSelection,
  swoTrackingCILOfferId: number
): SubmitSelectionStartAction {
  return {
    type: CILNotificationActionType.SubmitSelectionStart,
    selection: selection,
    swoTrackingCILOfferId: swoTrackingCILOfferId
  };
}



// WEBPACK FOOTER //
// ./src/cil-notification/cil-notification.actions.tsx